import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Users, BookOpen, ShoppingCart, IndianRupee, TrendingUp,
  Target, Trophy, BarChart3, PieChart, Activity, Award
} from 'lucide-react';
import { toast } from 'sonner';
import ThemeToggle from '../components/ThemeToggle';

export default function Analytics() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [testsByCategory, setTestsByCategory] = useState([]);
  const [topTests, setTopTests] = useState([]);
  const [scoreDistribution, setScoreDistribution] = useState([]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const [overviewRes, categoryRes, topRes, scoreRes] = await Promise.all([
        api.get('/admin/analytics/overview'),
        api.get('/admin/analytics/tests-by-category'),
        api.get('/admin/analytics/top-tests?limit=5'),
        api.get('/admin/analytics/score-distribution')
      ]);
      
      setOverview(overviewRes.data);
      setTestsByCategory(categoryRes.data);
      setTopTests(topRes.data);
      setScoreDistribution(scoreRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchAnalytics();
  }, [user, navigate, fetchAnalytics]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const categoryColors = {
    'JEE Mains': 'from-blue-500 to-cyan-500',
    'JEE Main': 'from-blue-500 to-cyan-500',
    'JEE Advanced': 'from-purple-500 to-pink-500',
    'NEET': 'from-green-500 to-emerald-500'
  };

  return (
    <div className="min-h-screen bg-background" data-testid="analytics-page">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin
            </Button>
            <div className="h-6 w-px bg-border"></div>
            <span className="font-semibold text-primary flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Performance Analytics
            </span>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-outfit font-bold mb-2">Performance Analytics</h1>
          <p className="text-muted-foreground">Track platform performance, user engagement, and test metrics</p>
        </motion.div>

        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl p-5 text-white shadow-lg"
          >
            <Users className="w-6 h-6 mb-2 opacity-80" />
            <div className="text-2xl font-bold font-mono">{overview?.total_users || 0}</div>
            <div className="text-xs opacity-80">Total Users</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl p-5 text-white shadow-lg"
          >
            <BookOpen className="w-6 h-6 mb-2 opacity-80" />
            <div className="text-2xl font-bold font-mono">{overview?.total_tests || 0}</div>
            <div className="text-xs opacity-80">Test Series</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-5 text-white shadow-lg"
          >
            <ShoppingCart className="w-6 h-6 mb-2 opacity-80" />
            <div className="text-2xl font-bold font-mono">{overview?.total_purchases || 0}</div>
            <div className="text-xs opacity-80">Purchases</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl p-5 text-white shadow-lg"
          >
            <IndianRupee className="w-6 h-6 mb-2 opacity-80" />
            <div className="text-2xl font-bold font-mono">₹{(overview?.total_revenue || 0).toLocaleString()}</div>
            <div className="text-xs opacity-80">Revenue</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl p-5 text-white shadow-lg"
          >
            <Activity className="w-6 h-6 mb-2 opacity-80" />
            <div className="text-2xl font-bold font-mono">{overview?.total_attempts || 0}</div>
            <div className="text-xs opacity-80">Test Attempts</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl p-5 text-white shadow-lg"
          >
            <Target className="w-6 h-6 mb-2 opacity-80" />
            <div className="text-2xl font-bold font-mono">{overview?.avg_score || 0}%</div>
            <div className="text-xs opacity-80">Avg Score</div>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Tests by Category */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-card border border-border rounded-2xl p-6"
          >
            <div className="flex items-center gap-2 mb-6">
              <PieChart className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-outfit font-bold">Tests by Exam Type</h2>
            </div>
            
            <div className="space-y-4">
              {testsByCategory.map((item, index) => {
                const examType = item._id || 'Unknown';
                const colorClass = categoryColors[examType] || 'from-gray-500 to-gray-600';
                const totalTests = testsByCategory.reduce((sum, t) => sum + t.count, 0);
                const percentage = totalTests > 0 ? Math.round((item.count / totalTests) * 100) : 0;
                
                return (
                  <div key={index} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{examType}</span>
                      <span className="text-sm text-muted-foreground">
                        {item.count} tests • {item.total_purchases || 0} purchases
                      </span>
                    </div>
                    <div className="h-3 bg-muted rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${percentage}%` }}
                        transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                        className={`h-full bg-gradient-to-r ${colorClass} rounded-full`}
                      />
                    </div>
                  </div>
                );
              })}
              
              {testsByCategory.length === 0 && (
                <p className="text-muted-foreground text-center py-8">No data available</p>
              )}
            </div>
          </motion.div>

          {/* Score Distribution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="bg-card border border-border rounded-2xl p-6"
          >
            <div className="flex items-center gap-2 mb-6">
              <BarChart3 className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-outfit font-bold">Score Distribution</h2>
            </div>
            
            <div className="flex items-end gap-2 h-48">
              {scoreDistribution.map((item, index) => {
                const maxCount = Math.max(...scoreDistribution.map(s => s.count), 1);
                const height = (item.count / maxCount) * 100;
                const colors = [
                  'from-red-500 to-red-600',
                  'from-orange-500 to-orange-600',
                  'from-yellow-500 to-yellow-600',
                  'from-green-500 to-green-600',
                  'from-emerald-500 to-emerald-600'
                ];
                
                return (
                  <div key={index} className="flex-1 flex flex-col items-center gap-2">
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: `${height}%` }}
                      transition={{ duration: 0.8, delay: 0.6 + index * 0.1 }}
                      className={`w-full bg-gradient-to-t ${colors[index]} rounded-t-lg min-h-[20px]`}
                    />
                    <span className="text-xs font-medium text-muted-foreground">{item.range}</span>
                    <span className="text-xs font-bold">{item.count}</span>
                  </div>
                );
              })}
              
              {scoreDistribution.length === 0 && (
                <p className="text-muted-foreground text-center w-full py-8">No attempts yet</p>
              )}
            </div>
          </motion.div>
        </div>

        {/* Top Tests */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-card border border-border rounded-2xl p-6"
        >
          <div className="flex items-center gap-2 mb-6">
            <Trophy className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-outfit font-bold">Top Performing Tests</h2>
          </div>
          
          <div className="space-y-3">
            {topTests.map((test, index) => {
              const colorClass = categoryColors[test.exam_type] || categoryColors[test.category] || 'from-gray-500 to-gray-600';
              
              return (
                <motion.div
                  key={test.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  className="flex items-center gap-4 p-4 bg-muted/30 rounded-xl hover:bg-muted/50 transition-colors"
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colorClass} flex items-center justify-center text-white font-bold`}>
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">{test.title}</h3>
                    <div className="text-sm text-muted-foreground">
                      {test.exam_type || test.category} • {test.subject || 'General'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-primary">{test.purchase_count || 0} sales</div>
                    <div className="text-sm text-muted-foreground">₹{test.price}</div>
                  </div>
                </motion.div>
              );
            })}
            
            {topTests.length === 0 && (
              <p className="text-muted-foreground text-center py-8">No tests available</p>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
